/**
 * P3 — Hint when PLATEAU detail layers are visible in the panel but deferred
 * by stable mount policy until the camera reaches street level.
 */
import type { CSSProperties } from 'react';
import { tokens } from '@/design';

export interface PlateauDetailDeferredChipProps {
  /** Number of visible layers not yet mounted (0 = hidden). */
  readonly deferredCount: number;
  /** When true, detail layers are allowed — chip stays hidden. */
  readonly streetDetailActive: boolean;
}

export function PlateauDetailDeferredChip({
  deferredCount,
  streetDetailActive,
}: PlateauDetailDeferredChipProps) {
  if (streetDetailActive || deferredCount <= 0) return null;

  const label =
    deferredCount === 1
      ? `Zoom in to ≤1 km to load 1 detail layer (roads, furniture, vegetation…)`
      : `Zoom in to ≤1 km to load ${deferredCount} detail layers (roads, furniture, vegetation…)`;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="plateau-detail-deferred-chip"
      style={chipStyle}
      title="Detail layers stay off at city scale so building tiles keep cache budget. Zoom closer to stream them."
    >
      <span aria-hidden style={iconStyle}>↘</span>
      <span>{label}</span>
    </div>
  );
}

const chipStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: tokens.space[1],
  marginTop: tokens.space[1],
  padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
  borderRadius: tokens.radius.pill,
  background: tokens.color.surface[1],
  color: tokens.color.ink.primary,
  border: `${tokens.border.width.thin}px solid ${tokens.color.semantic.warning}`,
  fontSize: 12,
  lineHeight: 1.35,
  maxWidth: 280,
  whiteSpace: 'normal',
};

const iconStyle: CSSProperties = {
  flexShrink: 0,
  fontSize: 14,
  lineHeight: 1,
};
